<template>
  <div class="counterField formField" :class="{isError, isRequired, isInline, isSmall, isDisabled: disabled}">
    <FormLabel v-bind="formLabelProps">
      <slot name="tooltip" />
    </FormLabel>
    <div class="conterField__Controls">
      <button type="button" @click="down" :disabled="disabled || isAtMin" :class="{'isDisable': isAtMin, isDisabled: disabled}">
        <Icon name="minus" />
      </button>
      <input
        v-if="editable"
        :id="uniqueID"
        class="counterField__Input"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        :value="modelValue ?? ''"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="setValue"
        @blur="setMinValue"
      />
      <div v-else class="counterField__Value">{{ modelValue }}</div>
      <button type="button" @click="up" :disabled="disabled || isAtMax" :class="{'isDisable': isAtMax, isDisabled: disabled}">
        <Icon name="plus" />
      </button>
    </div>
    <FormError :errors="mergedErrors" className="counterField__Error" />
  </div>
</template>

<script setup>
  const emit = defineEmits(['update:modelValue'])
  const props = defineProps({
    ...useFormFieldProps(),
    modelValue: {
      type: [Number, String],
    },
    minValue: {
      type: [Number, String],
      default: 0,
    },
    maxValue: {
      type: [Number, String],
    },
    small: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '–',
    },
  })

  const {
    isError,
    isDisabled,
    isRequired,
    isInline,
    hasValue,
    fieldValue,
    mergedErrors,
    uniqueID,
    formLabelProps,
    triggerValidate,
  } = useFormField(props, emit)

  defineExpose({
    triggerValidate,
  })

  const isSmall = computed(() => props.small)

  const isEmpty = computed(() => [null, undefined, ''].includes(props.modelValue))

  const isAtMin = computed(() => isEmpty.value || (props.minValue != null && Number(props.modelValue) <= Number(props.minValue)))

  const isAtMax = computed(() => !isEmpty.value && props.maxValue != null && Number(props.modelValue) >= Number(props.maxValue))

  function down() {
    if (isAtMin.value) return;
    emit('update:modelValue', Number(props.modelValue) - 1)
  }

  function up() {
    if (isAtMax.value) return;
    emit('update:modelValue', isEmpty.value ? Number(props.minValue ?? 0) : Number(props.modelValue) + 1)
  }

  function setValue(event) {
    const digits = event.target.value.replace(/\D/g, '');
    let value = digits === '' ? '' : Number(digits);
    if (value !== '' && props.maxValue != null) value = Math.min(value, Number(props.maxValue));
    event.target.value = value;
    emit('update:modelValue', value);
  }

  function setMinValue(event) {
    if (props.minValue == null) return;
    const value = event.target.value === '' ? Number(props.minValue) : Math.max(Number(event.target.value), Number(props.minValue));
    event.target.value = value;
    emit('update:modelValue', value);
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/components/forms/_counterField.scss');
</style>
